import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNutrients, useCreateNutrient, useUpdateNutrient, useDeleteNutrient } from '../../hooks/useNutrients';
import { schoolService, nutrientService } from '../../utils/api'; // Import nutrientService
import Modal from '../Modal';
import toast from 'react-hot-toast';

/**
 * NutrientsTab component for managing nutrients in the settings page
 * 
 * @param {Object} props Component props
 * @param {number} props.schoolId The school ID
 * @param {Array} props.selectedLanguages List of languages selected for the school
 * @param {Object} props.schoolInfo School information including defaultLanguageId
 * @param {Object} props.updateSchoolSettingsMutation The mutation hook for updating school settings
 */
const NutrientsTab = ({ 
  schoolId, 
  selectedLanguages, 
  schoolInfo, 
  updateSchoolSettingsMutation // Receive mutation hook as prop
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language; // Get current language code
  
  // React Query hooks with refetch function
  const { 
    data: nutrientsData = [], 
    isLoading: isNutrientsLoading, 
    isError: isNutrientsError,
    refetch: refetchNutrients
  } = useNutrients(schoolId);
  
  const createNutrientMutation = useCreateNutrient();
  const updateNutrientMutationHook = useUpdateNutrient();
  const deleteNutrientMutation = useDeleteNutrient();
  
  // State for the show_nutrients setting
  const [showNutrients, setShowNutrients] = useState(schoolInfo.show_nutrients || false);
  const [isSavingNutrientSetting, setIsSavingNutrientSetting] = useState(false);
  
  // Effect to sync local state with prop changes
  useEffect(() => {
    setShowNutrients(schoolInfo.show_nutrients || false);
  }, [schoolInfo.show_nutrients]);
  
  // State for new nutrient form
  const [newNutrient, setNewNutrient] = useState({
    is_enabled: true,
    translations: {},
    unit: '' // Add unit field
  });
  
  // State for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [nutrientToEdit, setNutrientToEdit] = useState(null);
  const [isFetchingEditData, setIsFetchingEditData] = useState(false); // Add loading state
  
  // State for delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [nutrientToDelete, setNutrientToDelete] = useState(null);
  
  // Open edit modal for a nutrient - Fetch full data
  const openEditModal = async (nutrientId) => {
    if (!schoolId) {
      toast.error(t('errors.schoolIdMissing', 'School ID is missing.'));
      return;
    }
    setIsFetchingEditData(true);
    setEditModalOpen(true); // Open modal immediately to show loading
    setNutrientToEdit(null); // Clear previous data
    
    try {
      console.log(`[NutrientsTab] Fetching details for nutrient ID: ${nutrientId}, School ID: ${schoolId}`);
      const fullNutrientData = await nutrientService.getNutrient(nutrientId, schoolId);
      console.log('[NutrientsTab] Fetched full nutrient data:', fullNutrientData);
      
      // The getNutrient service already returns translations as an object { lang_code: name }
      setNutrientToEdit({
        id: fullNutrientData.id,
        is_enabled: fullNutrientData.is_enabled,
        translations: fullNutrientData.translations || {}, // Ensure translations is an object
        unit: fullNutrientData.unit || '' // Add unit, default to empty string
      });
      
    } catch (error) {
      console.error('Error fetching nutrient details for edit:', error);
      toast.error(t('errors.fetchNutrientDetailsFailed', 'Failed to load nutrient details.'));
      closeEditModal(); // Close modal on error
    } finally {
      setIsFetchingEditData(false);
    }
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setNutrientToEdit(null); // Clear data on close
    setIsFetchingEditData(false); // Reset loading state
  };
  
  // Open delete modal for a nutrient
  const openDeleteModal = (nutrient) => {
    setNutrientToDelete(nutrient);
    setDeleteModalOpen(true);
  };
  
  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setNutrientToDelete(null);
  };
  
  // Handle translation changes for new nutrient
  const handleNutrientTranslationChange = (langCode, value) => {
    setNewNutrient(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: value
      }
    }));
  };
  
  // Handle unit change for new nutrient
  const handleNewNutrientUnitChange = (e) => {
    setNewNutrient(prev => ({ ...prev, unit: e.target.value }));
  };
  
  // Handle editing translation changes
  const handleEditTranslationChange = (langCode, value) => {
    setNutrientToEdit(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: value
      }
    }));
  };
  
  // Handle editing unit changes
  const handleEditUnitChange = (e) => {
    setNutrientToEdit(prev => ({ ...prev, unit: e.target.value }));
  };
  
  // Handle creating a new nutrient
  const handleNutrientSubmit = async (e) => {
    e.preventDefault();
    
    // Check for required default language translation
    const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
    
    if (!defaultLangCode || !newNutrient.translations[defaultLangCode]) {
      alert(t('settings.defaultLanguageRequired'));
      return;
    }
    
    // Convert translations object to the array format expected by the backend
    const formattedTranslations = Object.entries(newNutrient.translations)
      .map(([langCode, name]) => ({
        language_code: langCode,
        name: name
      }));
    
    // Ensure we only send non-empty translations
    const validTranslations = formattedTranslations.filter(t => t.name && t.name.trim() !== '');
    
    if (validTranslations.length === 0) {
      alert(t('settings.atLeastOneTranslationRequired', 'Please provide at least one translation.'));
      return;
    }

    try {
      await createNutrientMutation.mutateAsync({
        nutrient: {
          is_enabled: newNutrient.is_enabled,
          translations: validTranslations, // Send the formatted array
          unit: newNutrient.unit || null // Send unit, use null if empty
        },
        schoolId
      });
      
      // Reset form
      setNewNutrient({
        is_enabled: true,
        translations: {},
        unit: '' // Reset unit
      });
    } catch (error) {
      console.error('Error creating nutrient:', error);
      // Note: onError in the hook handles the toast
    }
  };
  
  // Handle updating a nutrient
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Check for required default language translation
    const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
    
    if (!defaultLangCode || !nutrientToEdit.translations[defaultLangCode]) {
      alert(t('settings.defaultLanguageRequired'));
      return;
    }
    
    // Convert translations object to the array format expected by the backend
    const formattedTranslations = Object.entries(nutrientToEdit.translations)
      .map(([langCode, name]) => ({
        language_code: langCode,
        name: name
      }));
      
    // Ensure we only send non-empty translations
    const validTranslations = formattedTranslations.filter(t => t.name && t.name.trim() !== '');

    if (validTranslations.length === 0) {
      alert(t('settings.atLeastOneTranslationRequired', 'Please provide at least one translation.'));
      return;
    }

    try {
      await updateNutrientMutationHook.mutateAsync({
        nutrientId: nutrientToEdit.id,
        nutrient: {
          is_enabled: nutrientToEdit.is_enabled,
          translations: validTranslations, // Send the formatted array
          unit: nutrientToEdit.unit || null // Send unit, use null if empty
        },
        schoolId
      });
      
      closeEditModal();
    } catch (error) {
      console.error('Error updating nutrient:', error);
      // Note: onError in the hook handles the toast
    }
  };
  
  // Handle deleting a nutrient
  const handleDeleteNutrient = async () => {
    if (!nutrientToDelete) return;
    
    try {
      await deleteNutrientMutation.mutateAsync({
        nutrientId: nutrientToDelete.id,
        schoolId
      });
      
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting nutrient:', error);
      // Note: onError in the hook handles the toast
    }
  };
  
  // Handler to save the show_nutrients setting using the mutation from props
  const handleSaveNutrientSetting = async () => {
    setIsSavingNutrientSetting(true);
    
    try {
      // Use the mutation hook passed via props
      await updateSchoolSettingsMutation.mutateAsync({
        settings: { show_nutrients: showNutrients },
        schoolId
      });
      
      // No need to manually update schoolInfo prop here
      // The parent's React Query cache invalidation will handle updating the prop
      
      toast.success(t('settings.settingsSaved'));
    } catch (error) {
      console.error('Error saving nutrient setting:', error);
      toast.error(t('settings.errorSavingSetting'));
      // Revert local state if save fails?
      setShowNutrients(schoolInfo.show_nutrients || false); 
    } finally {
      setIsSavingNutrientSetting(false);
    }
  };
  
  return (
    <div>
      {/* Show Nutrients Setting */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold">{t('settings.nutrientDisplaySettings', 'Nutrient Display Settings')}</h2>
        <p className="text-sm text-gray-600 mt-2">
          {t('settings.nutritionalInformationExplanation', 'Choose whether nutritional information for each daily menu should be displayed in the menu viewer. If any of the daily menu\'s dishes are missing nutritional information, a message will be displayed instead saying nutritional information is not available.')}
        </p>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-nutrients-setting"
              checked={showNutrients}
              onChange={(e) => setShowNutrients(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="show-nutrients-setting" className="ml-2 block text-sm text-gray-700">
              {t('settings.showNutrients', 'Show nutritional information in menu viewer')}
            </label>
          </div>
          <button
            onClick={handleSaveNutrientSetting}
            disabled={isSavingNutrientSetting}
            className={`px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 ${
              isSavingNutrientSetting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSavingNutrientSetting ? t('settings.saving') : t('settings.saveChanges')}
          </button>
        </div>
      </div>
      
      {/* Header titles in a flex row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        <h2 className="text-xl font-semibold">{t('settings.nutrients', 'Nutrients')}</h2>
        <h3 className="text-xl font-semibold">{t('settings.addNewNutrient', 'Add New Nutrient')}</h3>
      </div>
      
      {/* Horizontal layout container */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nutrients list - left side */}
        <div className="space-y-2">
          {isNutrientsLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : isNutrientsError ? (
            <div className="p-4 bg-red-100 text-red-700 rounded mb-4">
              {t('nutrientutils.errorLoadingNutrients', 'Error loading nutrients. Please try again later.')}
            </div>
          ) : nutrientsData.length === 0 ? (
            <div className="p-4 bg-gray-100 text-gray-700 rounded mb-4">
              {t('nutrientutils.noNutrientsYet', 'No nutrients have been created yet.')}
            </div>
          ) : (
            nutrientsData.map((nutrient) => {
              // Find the school's default language code
              const defaultLangCode = selectedLanguages.find(lang => lang.id === schoolInfo.defaultLanguageId)?.code;
              
              let displayText = null;
              let displayLangCode = null;
              const translations = nutrient.translations || {};
              const unitText = nutrient.unit ? ` (${nutrient.unit})` : ''; // Format unit text

              // 1. Try current UI language
              if (translations[currentLanguage] && translations[currentLanguage].trim() !== '') {
                displayText = translations[currentLanguage] + unitText; // Add unit here
                displayLangCode = currentLanguage;
              } 
              // 2. Try school's default language (if different from current)
              else if (defaultLangCode && defaultLangCode !== currentLanguage && translations[defaultLangCode] && translations[defaultLangCode].trim() !== '') {
                displayText = translations[defaultLangCode] + unitText; // Add unit here
                displayLangCode = defaultLangCode;
              } 
              // 3. Try other selected languages
              else {
                for (const lang of selectedLanguages) {
                  if (lang.code !== currentLanguage && lang.code !== defaultLangCode && translations[lang.code] && translations[lang.code].trim() !== '') {
                    displayText = translations[lang.code] + unitText; // Add unit here
                    displayLangCode = lang.code;
                    break; // Found one, stop searching selected languages
                  }
                }
              }

              // 4. Fallback if no suitable translation found
              if (!displayText) {
                // Try finding the first available translation regardless of language selection
                const firstAvailableTranslation = Object.entries(translations).find(([, name]) => name && name.trim() !== '');
                if (firstAvailableTranslation) {
                  displayText = firstAvailableTranslation[1] + unitText; // Add unit here
                  displayLangCode = firstAvailableTranslation[0];
                } else {
                  // Last resort: use ID if no translations at all
                  displayText = `Nutrient #${nutrient.id}${unitText}`; // Add unit here
                  displayLangCode = null; // No specific language
                }
              }

              // 5. Final fallback string
              if (!displayText) {
                displayText = t('settings.noTranslationAvailable', 'No translation available');
                displayLangCode = null; // No language code available
              }

              const isFallback = displayLangCode !== null && displayLangCode !== currentLanguage;

              // Determine class based on enabled status and fallback status
              let nameClass = 'mr-3'; // Base margin
              if (!nutrient.is_enabled) {
                nameClass += ' text-gray-500 italic font-medium'; // Disabled style
              } else if (isFallback) {
                nameClass += ' font-normal italic'; // Fallback style
              } else {
                nameClass += ' font-medium'; // Default style
              }
              
              return (
                <div key={nutrient.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center flex-grow mr-4"> 
                    <span className={nameClass}>
                      {displayText}
                      {/* Display language code in gray if it's a fallback */}
                      {isFallback && displayLangCode && (
                        <span className="text-xs ml-1 text-gray-400">
                          ({displayLangCode.toUpperCase()})
                        </span>
                      )}
                    </span>

                    
                    {/* Language Indicator Labels */}
                    <div className="flex space-x-1 flex-shrink-0">
                      {selectedLanguages.map(lang => {
                        const hasTranslation = nutrient.translations && nutrient.translations[lang.code] && nutrient.translations[lang.code].trim() !== '';
                        const title = hasTranslation 
                          ? t(`languageNames.${lang.code}`) + ' (' + t('settings.translated') + ')' 
                          : t(`languageNames.${lang.code}`) + ' (' + t('tags.noTranslation') + ')';
                        
                        return (
                          <span 
                            key={lang.code}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold
                              ${hasTranslation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-500'}`}
                            title={title}
                          >
                            {lang.code.toUpperCase()}
                          </span>
                        );
                      })}
                    </div>

                    {!nutrient.is_enabled && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-300 text-gray-700">
                        {t('settings.disabled')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <button
                      className="text-blue-500 hover:text-blue-700 mr-3"
                      onClick={() => openEditModal(nutrient.id)} // Pass ID only
                    >
                      {t('settings.edit')}
                    </button>
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={() => openDeleteModal(nutrient)} // Keep passing the whole object for delete confirmation name
                    >
                      {t('settings.delete')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        
        {/* Add New Nutrient Form - right side */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <form onSubmit={handleNutrientSubmit} className="space-y-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-700">{t('settings.translations')}</h4>
              </div>
              {selectedLanguages.length > 0 ? (
                // Sort languages to show default language first, then others alphabetically
                [...selectedLanguages]
                  .sort((a, b) => {
                    // Put default language at the top
                    if (a.id === schoolInfo.defaultLanguageId) return -1;
                    if (b.id === schoolInfo.defaultLanguageId) return 1;
                    // Otherwise sort alphabetically by name
                    return a.name.localeCompare(b.name);
                  })
                  .map((lang) => {
                    const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                    return (
                      <div key={lang.code} className="flex items-start">
                        <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                          {t(`languageNames.${lang.code}`)} ({lang.code})
                          {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className="flex-grow relative">
                          <input
                            type="text"
                            name={`translation-${lang.code}`}
                            value={newNutrient.translations[lang.code] || ''}
                            onChange={(e) => handleNutrientTranslationChange(lang.code, e.target.value)}
                            placeholder={isDefaultLanguage ? 
                              t('settings.requiredTranslation') : 
                              t('settings.enterTranslation')
                            }
                            className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                              ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                            required={isDefaultLanguage}
                          />
                          {isDefaultLanguage && (
                            <span className="text-xs text-blue-600 mt-1 block">
                              {t('settings.defaultLanguageField')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm text-gray-500">{t('settings.noTranslationsAvailable')}</p>
              )}
            </div>
            <div className="mt-4 flex items-start">
              <span className={`text-sm mt-2 mr-2`}>
                {t('settings.nutrientUnit', 'Unit (g, mg, kcal...)')}
              </span>
              <div className="flex-grow relative">
                <input
                  type="text"
                  id="new-nutrient-unit"
                  value={newNutrient.unit}
                  onChange={handleNewNutrientUnitChange}
                  placeholder={t('settings.optionalUnitPlaceholder', 'Input nutrient unit')}
                  className="flex-grow border border-gray-300 rounded-md shadow-sm p-2 w-full"
                />
              </div>
            </div>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="nutrient-enabled"
                checked={newNutrient.is_enabled}
                onChange={(e) => setNewNutrient(prev => ({...prev, is_enabled: e.target.checked}))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="nutrient-enabled" className="ml-2 block text-sm text-gray-700">
                {t('settings.enabledNutrient', 'Show this nutrient in the menu viewer')}
              </label>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                disabled={createNutrientMutation.isPending}
              >
                {createNutrientMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('settings.adding')}
                  </div>
                ) : (
                  t('settings.add')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Edit Nutrient Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title={t('settings.editNutrient')}
      >
        {isFetchingEditData ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : nutrientToEdit ? (
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-700">{t('settings.translations')}</h4>
              </div>
              {selectedLanguages.length > 0 ? (
                [...selectedLanguages]
                  .sort((a, b) => {
                    if (a.id === schoolInfo.defaultLanguageId) return -1;
                    if (b.id === schoolInfo.defaultLanguageId) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((lang) => {
                    const isDefaultLanguage = lang.id === schoolInfo.defaultLanguageId;
                    return (
                      <div key={lang.code} className="flex items-start">
                        <span className={`w-24 text-sm ${isDefaultLanguage ? 'font-semibold' : ''} mt-2`}>
                          {t(`languageNames.${lang.code}`)} ({lang.code})
                          {isDefaultLanguage && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className="flex-grow relative">
                          <input
                            type="text"
                            name={`edit-translation-${lang.code}`}
                            value={nutrientToEdit.translations[lang.code] || ''} // Use nutrientToEdit state
                            onChange={(e) => handleEditTranslationChange(lang.code, e.target.value)}
                            placeholder={isDefaultLanguage ? 
                              t('settings.requiredTranslation') : 
                              t('settings.enterTranslation')
                            }
                            className={`flex-grow border ${isDefaultLanguage ? 'border-blue-300' : 'border-gray-300'} rounded-md shadow-sm p-2 w-full
                              ${isDefaultLanguage ? 'bg-blue-50' : ''}`}
                            required={isDefaultLanguage}
                          />
                          {isDefaultLanguage && (
                            <span className="text-xs text-blue-600 mt-1 block">
                              {t('settings.defaultLanguageField')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-sm text-gray-500">{t('settings.noTranslationsAvailable')}</p>
              )}
             </div>
             <div className="mt-4 flex items-start">
                <span className={`text-sm mt-2 mr-2`}>
                 {t('settings.nutrientUnit', 'Unit (g, mg, kcal...)')}
               </span>
               <div className="flex-grow relative">
                <input
                  type="text"
                  id="edit-nutrient-unit"
                  value={nutrientToEdit.unit}
                  onChange={handleEditUnitChange}
                  placeholder={t('settings.optionalUnitPlaceholder', 'Input nutrient unit')}
                  className="flex-grow border border-gray-300 rounded-md shadow-sm p-2 w-full"
                />
             </div>
            </div>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="edit-nutrient-enabled"
                checked={nutrientToEdit.is_enabled} // Use nutrientToEdit state
                onChange={(e) => setNutrientToEdit(prev => ({...prev, is_enabled: e.target.checked}))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="edit-nutrient-enabled" className="ml-2 block text-sm text-gray-700">
                {t('settings.enabledNutrient', 'Show this nutrient in the menu viewer')}
              </label>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeEditModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                disabled={updateNutrientMutationHook.isPending}
              >
                {updateNutrientMutationHook.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('settings.saving')}
                  </div>
                ) : (
                  t('settings.save')
                )}
              </button>
            </div>
          </form>
        ) : null /* Render null if not loading and no nutrientToEdit data yet */}
      </Modal>
      
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && nutrientToDelete && (
        <Modal
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          title={t('settings.deleteNutrient')}
        >
          <div className="p-4">
            <p className="text-gray-700 mb-4">
              {t('settings.deleteNutrientConfirmation', 
                'Are you sure you want to delete this nutrient? This action cannot be undone.')}
            </p>
            <p className="font-semibold mb-6">
              {nutrientToDelete.name}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteNutrient}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                disabled={deleteNutrientMutation.isPending}
              >
                {deleteNutrientMutation.isPending ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    {t('settings.deleting')}
                  </div>
                ) : (
                  t('settings.delete')
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default NutrientsTab; 